<template>
  <div>
    <h2
      class="mt-0 tw-text-2xl tw-font-semibold tw-mb-2"
      style="color: var(--text-color)"
    >
      {{ sellerId ? "Update" : "Add" }} Seller
    </h2>
    <form @submit.prevent="confirmCPMHighBeforeSubmit()">
      <Card class="mb-3">
        <template #title>
          <div class="text-xl">Seller Information</div>
        </template>
        <template #content>
          <div class="grid">
            <div class="flex flex-column gap-1 col-12 md:col-6">
              <label for="name">Name </label>
              <InputText
                id="name"
                v-model="form.name"
                :class="{ 'p-invalid': submitted && !form.name }"
              />
              <small v-if="submitted && !form.name" class="p-error"
                >Name is required</small
              >
            </div>
            <div class="flex flex-column gap-1 col-12 md:col-6">
              <label for="email">Email</label>
              <InputText
                id="email"
                type="email"
                v-model="form.email"
                :class="{
                  'p-invalid':
                    submitted &&
                    (!form.email || !emailValid) &&
                    !form.sellerGroup,
                }"
              />
              <small
                v-if="submitted && !emailValid && !form.sellerGroup"
                class="p-error"
                >Invalid email format</small
              >
            </div>

            <div
              v-if="!store.getters.isGroupAdminLoggedIn"
              class="flex flex-column gap-1 col-12 md:col-6"
            >
              <label for="sellerGroup">Seller Group</label>
              <Dropdown
                id="sellerGroup"
                v-model="form.sellerGroup"
                :options="sellerGroups"
                :class="{ 'p-invalid': submitted && !form.sellerGroup }"
                placeholder="Select or Enter Seller Group"
                optionLabel="label"
                optionValue="value"
                filter
                showClear
              />
              <small v-if="submitted && !form.sellerGroup" class="p-error"
                >Group is required</small
              >
            </div>
            <div
              class="flex flex-column gap-1 col-12 md:col-6"
              v-if="shouldShowConnatixFields"
            >
              <label for="connatix_player_id">Connatix Player ID</label>
              <InputText
                id="connatix_player_id"
                v-model="form.connatix_player_id"
                :class="{
                  'p-invalid': submitted && !connatixPlayerIdValid,
                }"
                placeholder="Player UUID"
              />
              <small v-if="submitted && !connatixPlayerIdValid" class="p-error"
                >Connatix Player ID is required</small
              >
            </div>
            <div
              class="flex flex-column gap-1 col-12 md:col-6"
              v-if="shouldShowConnatixFields"
            >
              <label for="connatix_player_share"
                >Video Revshare (0.01 - 1.00 of revenue, optional)</label
              >
              <InputText
                id="connatix_player_share"
                type="number"
                inputmode="decimal"
                step="0.01"
                min="0.01"
                max="1"
                v-model.number="form.connatix_player_share"
                :class="{
                  'p-invalid': submitted && !connatixPlayerShareValid,
                }"
                placeholder="e.g. 0.60"
              />
              <small
                v-if="submitted && !connatixPlayerShareValid"
                class="p-error"
              >
                Provide a share between 0.01 and 1.00 when set
              </small>
            </div>

            <div class="flex flex-column gap-1 col-12 md:col-6">
              <label for="domain">Domain </label>
              <InputText
                id="domain"
                v-model="form.domain"
                :class="{
                  'p-invalid': submitted && (!form.domain || !domainValid),
                }"
              />
              <small v-if="submitted && !domainValid" class="p-error"
                >Invalid domain format</small
              >
            </div>

            <div class="flex flex-column gap-1 col-12 md:col-6">
              <label for="password">Password</label>
              <InputText
                id="password"
                autocomplete="new-password"
                type="password"
                v-model="form.password"
                placeholder="Enter password"
                :class="{
                  'p-invalid':
                    submitted &&
                    (!form.password || !passwordValid) &&
                    !form.sellerGroup,
                }"
              />
              <small
                v-if="submitted && !passwordValid && !form.sellerGroup"
                class="p-error"
                >Password must be atleast 8 chars, with upper-case and
                lower-case letters</small
              >
            </div>
            <div
              class="flex flex-column gap-1 col-12 md:col-6"
              v-if="
                store.getters.isAdminLoggedIn &&
                form.contentMediaType !== 'snippet'
              "
            >
              <label for="tier">Tier:</label>
              <Dropdown
                id="tier"
                v-model="form.tier"
                :options="tierOptions"
                placeholder="Select Tier"
              />
            </div>
            <div
              class="flex flex-column gap-1 col-12 md:col-6"
              v-if="store.getters.isAdminLoggedIn"
            >
              <label for="seller_classification">Seller Classification</label>
              <Dropdown
                id="seller_classification"
                v-model="form.classification"
                :options="classificationOptions"
                optionLabel="label"
                optionValue="value"
                placeholder="Select Classification"
                showClear
              />
            </div>
            <div
              class="flex flex-column gap-1 col-12 md:col-6"
              v-if="store.getters.isAdminLoggedIn"
            >
              <label for="site_categories">Site Categories</label>
              <MultiSelect
                input-id="site_categories"
                v-model="form.site_categories"
                :options="categories"
                display="chip"
                placeholder="Select Categories"
                filter
                filter-placeholder="Search Categories"
              />
            </div>
          </div>
        </template>
      </Card>
      <Card class="mb-3">
        <template #title>
          <div class="text-xl">Ads.txt Lines</div>
        </template>
        <template #content>
          <div class="flex flex-wrap align-items-center gap-3 mb-3">
            <SelectButton
              v-model="adsTxtTier"
              :options="adsTxtTierOptions"
              optionLabel="label"
              optionValue="value"
              :allowEmpty="false"
            />
            <Tag
              :value="shouldShowConnatixFields ? 'AUDIO + VIDEO' : 'AUDIO'"
              severity="info"
            />
            <Button
              type="button"
              icon="pi pi-copy"
              label="Copy Lines"
              size="small"
              outlined
              :disabled="!generatedAdsTxtLines"
              @click="copyAdsTxtLines"
            />
          </div>
          <div class="adstxt-code-container">
            <pre>{{ generatedAdsTxtLines || "No matching ads.txt lines" }}</pre>
          </div>
          <small class="text-500">
            Give these lines to the publisher for their domain's ads.txt file.
            Audio lines are included by default; video lines are added
            automatically for video-enabled sellers. Display and app lines are
            excluded.
          </small>
        </template>
      </Card>
      <div class="mb-3">
        <div class="grid align-items-stretch">
          <div
            v-if="
              !store.getters.isGroupAdminLoggedIn &&
              form.contentMediaType !== 'snippet'
            "
            class="col-12 md:col-6"
          >
            <ContractDetailsCard
              v-model:contractType="form.contractType"
              v-model:contractValue="form.contractValue"
              :cpm-per-geo="form.cpm_per_geo"
              :contract-types="contractTypes"
              :country-list="countryList"
              :contact-value-label="contactValueLabel"
              :submitted="submitted"
              :is-valid="validateContractDetails()"
              @contract-type-change="onContractTypeChange"
              @add-geo="form.cpm_per_geo.push({ geo: '', cpm: 0 })"
              @remove-geo="removeGeo"
            />
          </div>
          <div class="col-12 md:col-6">
            <Card class="h-full">
              <template #title>
                <div class="text-xl">Content</div>
              </template>
              <template #content>
                <div class="grid">
                  <div class="flex flex-column gap-1 col-12">
                    <ContentMediaTypeSelector v-model="form.contentMediaType" />
                  </div>
                  <div
                    class="flex flex-column gap-1 col-12"
                    v-if="form.contentMediaType === 'snippet'"
                  >
                    <RestrictedValidationToggle
                      v-model="form.restrictedValidation"
                    />
                  </div>
                  <div class="flex flex-column gap-1 col-12">
                    <label for="contentType">Content Source</label>
                    <Dropdown
                      id="contentType"
                      v-model="form.contentType"
                      :options="contentTypes"
                      optionLabel="label"
                      optionValue="value"
                      placeholder="Select Content Type"
                    />
                  </div>
                  <div
                    class="flex flex-column gap-1 col-12"
                    v-if="form.contentType === 'categories'"
                  >
                    <label for="categories">Categories</label>
                    <MultiSelect
                      input-id="categories"
                      v-model="form.categories"
                      :options="categories"
                      display="chip"
                      placeholder="Select Categories"
                      filter
                      filter-placeholder="Search Categories"
                    />
                  </div>

                  <div
                    class="flex flex-column gap-1 col-12"
                    v-if="form.contentType === 'rss_feed'"
                  >
                    <label for="rssFeedUrl">RSS Feed URL</label>
                    <InputText
                      id="rssFeedUrl"
                      v-model="form.rssFeedUrl"
                      :class="{
                        'p-invalid':
                          submitted && (!form.rssFeedUrl || !rssFeedUrlValid),
                      }"
                    />
                    <small v-if="submitted && !rssFeedUrlValid" class="p-error">
                      Invalid RSS Feed URL format
                    </small>
                  </div>

                  <div
                    class="flex flex-column gap-1 col-12"
                    v-if="form.contentType === 'playlist'"
                  >
                    <label for="playlists">Playlists</label>
                    <Dropdown
                      v-if="playlistsOptions.length"
                      id="playlists"
                      v-model="form.playlists"
                      :options="playlistsOptions"
                      optionLabel="name"
                      optionValue="uuid"
                      placeholder="Select Playlists"
                      multiple
                    />
                  </div>

                  <div
                    class="flex flex-column gap-1 col-12"
                    v-if="form.contentType === 'shows'"
                  >
                    <label for="showSelection">Select Shows</label>
                    <MultiSelect
                      id="showSelection"
                      v-model="form.selectedShows"
                      :options="podcastShowsOptions"
                      optionLabel="label"
                      optionValue="value"
                      placeholder="Select Shows"
                      display="chip"
                      filter
                      :filter-fields="['label']"
                      filter-placeholder="Search Shows"
                      :panelStyle="{ maxWidth: 'min(500px, 45vw)' }"
                    />
                    <DisplayOrderSelector
                      v-if="
                        form.contentMediaType === 'snippet' &&
                        form.selectedShows.length
                      "
                      v-model="displayOrder"
                      class="mt-2"
                    />
                    <div
                      v-if="
                        form.contentMediaType === 'snippet' &&
                        form.selectedShows.length
                      "
                      class="flex flex-column gap-3 mt-2"
                    >
                      <draggable
                        v-model="form.selectedShows"
                        :item-key="(id) => id"
                        handle=".drag-handle"
                        animation="150"
                        tag="div"
                        class="flex flex-column gap-3"
                        :disabled="displayOrder.shuffle"
                      >
                        <template #item="{ element: showId, index }">
                          <div
                            v-if="snippetShowConfigs[showId]"
                            class="show-order-card"
                          >
                            <div class="show-order-header">
                              <i
                                v-if="!displayOrder.shuffle"
                                class="pi pi-bars drag-handle flex-none"
                              ></i>
                              <span class="show-dot flex-none"></span>
                              <span class="show-title flex-1">{{
                                podcastShowsOptions.find(
                                  (s) => s.value === showId
                                )?.label
                              }}</span>
                              <span
                                v-if="!displayOrder.shuffle"
                                class="show-position-badge flex-none"
                                >{{ index + 1 }}</span
                              >
                            </div>
                            <div class="show-order-fields">
                              <div class="show-field">
                                <label class="text-xs text-500"
                                  >Snippets per ep (max
                                  {{
                                    podcastShowsOptions.find(
                                      (s) => s.value === showId
                                    )?.snippetsPerEpisodeConfig
                                  }})</label
                                >
                                <InputNumber
                                  v-model="
                                    snippetShowConfigs[showId]
                                      .snippets_per_episode
                                  "
                                  :min="1"
                                  :max="
                                    podcastShowsOptions.find(
                                      (s) => s.value === showId
                                    )?.snippetsPerEpisodeConfig || 1
                                  "
                                  :class="{
                                    'p-invalid': perEpisodeExceedsTotal(showId),
                                  }"
                                  class="w-full"
                                />
                              </div>
                              <div class="show-field">
                                <label class="text-xs text-500"
                                  >Total snippets</label
                                >
                                <InputNumber
                                  v-model="
                                    snippetShowConfigs[showId].snippets_count
                                  "
                                  :min="1"
                                  :max="MAX_TOTAL_SNIPPETS"
                                  class="w-full"
                                />
                              </div>
                            </div>
                            <small
                              v-if="perEpisodeExceedsTotal(showId)"
                              class="p-error"
                              >Snippets per ep can't exceed total
                              snippets</small
                            >
                          </div>
                        </template>
                      </draggable>

                      <small
                        :class="
                          totalSnippetsRequested > 25 ? 'p-error' : 'text-500'
                        "
                      >
                        Total snippets requested: {{ totalSnippetsRequested }}
                        / 25
                      </small>
                    </div>
                  </div>

                  <div
                    class="flex flex-column gap-1 col-12"
                    v-if="showMinEpisodeDuration"
                  >
                    <label
                      for="min_episode_duration"
                      class="flex align-items-center gap-2"
                    >
                      <span>Minimum Episode Length (sec)</span>
                      <i
                        class="pi pi-info-circle text-500 cursor-pointer"
                        v-tooltip.bottom="
                          'Episodes shorter than this are excluded from the widget and their slots are filled with the next matching episodes. Set 0 for no limit. Useful for skipping ~60s trailers.'
                        "
                      ></i>
                    </label>
                    <InputText
                      id="min_episode_duration"
                      type="number"
                      min="0"
                      step="1"
                      placeholder="0"
                      v-model="form.min_episode_duration"
                      :class="{
                        'p-invalid': submitted && !minEpisodeDurationValid,
                      }"
                    />
                    <small class="text-500">
                      In seconds — e.g. 120 for two minutes. 0 means no limit.
                    </small>
                    <small
                      v-if="submitted && !minEpisodeDurationValid"
                      class="p-error"
                    >
                      Must be a whole number of 0 or more
                    </small>
                  </div>
                </div>
              </template>
            </Card>
          </div>
          <div
            v-if="form.contentMediaType === 'snippet'"
            class="col-12 md:col-6 align-self-start"
          >
            <Card>
              <template #title>
                <div class="text-xl">CTA Options</div>
              </template>
              <template #content>
                <CtaOptionsSelector v-model="form.ctaMode" />
              </template>
            </Card>
          </div>
          <div
            v-if="form.contentMediaType !== 'snippet'"
            class="col-12 md:col-6"
          >
            <CustomSloganCard
              v-model:customSloganText="form.custom_slogan_text"
              v-model:customSloganLink="form.custom_slogan_link"
            />
          </div>
          <div
            v-if="form.contentMediaType !== 'snippet'"
            class="col-12 md:col-6"
          >
            <GeolocationRestrictionCard
              v-model:geoBl="form.geo_bl"
              v-model:geoWl="form.geo_wl"
              :country-list="countryList"
            />
          </div>
        </div>
      </div>
      <div class="w-full flex justify-content-end gap-2">
        <Button
          class="flex-1 sm:flex-initial"
          outlined
          label="Cancel"
          @click="router.go(-1)"
        />
        <Button
          class="flex-1 sm:flex-initial"
          type="submit"
          label="Submit"
          :disabled="!snippetShowsValid"
        />
      </div>
    </form>
  </div>
</template>

<script>
import { ref, onMounted, computed, watch, nextTick } from "vue";
import InputText from "primevue/inputtext";
import InputNumber from "primevue/inputnumber";
import Dropdown from "primevue/dropdown";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { useToast } from "primevue/usetoast";
import { useConfirm } from "primevue/useconfirm";

import Button from "primevue/button";
import Card from "primevue/card";
import MultiSelect from "primevue/multiselect";
import SelectButton from "primevue/selectbutton";
import Tag from "primevue/tag";
import axios from "axios";
import { API_BASE_URL, getContentTypeOptions } from "../../consts";
import ContentMediaTypeSelector from "../ContentMediaTypeSelector.vue";
import RestrictedValidationToggle from "../RestrictedValidationToggle.vue";
import CtaOptionsSelector from "../CtaOptionsSelector.vue";
import DisplayOrderSelector from "../DisplayOrderSelector.vue";
import ContractDetailsCard from "./shared/ContractDetailsCard.vue";
import CustomSloganCard from "./shared/CustomSloganCard.vue";
import GeolocationRestrictionCard from "./shared/GeolocationRestrictionCard.vue";
import draggable from "vuedraggable";

export default {
  components: {
    InputText,
    InputNumber,
    Dropdown,
    Button,
    Card,
    MultiSelect,
    SelectButton,
    Tag,
    ContentMediaTypeSelector,
    RestrictedValidationToggle,
    CtaOptionsSelector,
    DisplayOrderSelector,
    ContractDetailsCard,
    CustomSloganCard,
    draggable,
    GeolocationRestrictionCard,
  },

  setup() {
    const router = useRouter();
    const store = useStore();
    const toast = useToast();
    const confirm = useConfirm();

    const submitted = ref(false);
    const searchText = ref("");

    const emailValid = ref(true);
    const domainValid = ref(true);
    const rssFeedUrlValid = ref(true);
    const contractValueValid = ref(true);
    const passwordValid = ref(true);
    const connatixPlayerIdValid = ref(true);
    const connatixPlayerShareValid = ref(true);
    const partnerId = router.currentRoute.value.query.partnerId;
    const contentShowsOnly = ref(false);

    let localStorageKey = "token";

    if (store.getters.isGroupAdminLoggedIn) localStorageKey = "groupAdminToken";

    if (store.getters.isAdminLoggedIn) localStorageKey = "adminToken";

    const token = localStorage.getItem(localStorageKey);

    const contentTypes = computed(() =>
      getContentTypeOptions(
        [
          { label: "Categories", value: "categories" },
          { label: "RSS Feed", value: "rss_feed" },
          { label: "Playlists", value: "playlist" },
          { label: "Shows", value: "shows" },
        ],
        contentShowsOnly.value || form.value.contentMediaType === "snippet"
      )
    );

    // Not offered where the limit cannot be honoured: snippets and curated playlists
    const showMinEpisodeDuration = computed(
      () =>
        form.value.contentMediaType !== "snippet" &&
        ["categories", "rss_feed", "shows"].includes(form.value.contentType)
    );

    const minEpisodeDurationValid = computed(() => {
      const raw = form.value.min_episode_duration;
      if (raw === "" || raw === null || raw === undefined) return true;
      return Number.isInteger(Number(raw)) && Number(raw) >= 0;
    });

    const contractTypes = [
      { label: "Per Load", value: "cpm" },
      { label: "Revenue Share", value: "revshare" },
    ];

    const tierOptions = ["3", "2", "1"];
    const classificationOptions = [
      { label: "Hardcode (Fix Spot)", value: "hardcode_fix_spot" },
      { label: "AdServer - Price Priority", value: "adserver_price_priority" },
      { label: "AdServer - RON", value: "adserver_ron" },
      { label: "AdServer - Google - PP", value: "adserver_google_pp" },
      {
        label: "AdServer - Google - Fix Spot",
        value: "adserver_google_fix_spot",
      },
      { label: "AdServer - Other", value: "adserver_other" },
      { label: "Other", value: "other" },
    ];

    const form = ref({
      name: "",
      email: "",
      domain: "",
      categories: [],
      contractType: contractTypes[0].value,
      contractValue: 0,
      sellerId: null,
      contentMediaType: "podcast",
      restrictedValidation: false,
      ctaMode: "want_more",
      contentType: "categories",
      selectedShows: [],
      min_episode_duration: 0,
      rssFeedUrl: "",
      playlists: [],
      password: "",
      sellerGroup: store.getters.isGroupAdminLoggedIn
        ? JSON.parse(localStorage.groupAdmin).id
        : null,
      custom_slogan_text: "",
      custom_slogan_link: "",
      consent_approved: false,
      geo_bl: [],
      geo_wl: [],
      tier: "3",
      site_categories: [],
      cpm_per_geo: [],
      classification: null,
      connatix_player_id: "",
      connatix_player_share: null,
    });

    const playlistsOptions = ref([]);
    const sellerGroups = ref([]);
    const categories = ref([]);
    const initialRSSUrl = ref("");
    const countryList = ref([]);
    const podcastShowsOptions = ref([]);
    const allShowsList = ref([]);
    const initialContractValue = ref(0);
    const snippetShowConfigs = ref({});
    const displayOrder = ref({ shuffle: false });

    const MAX_TOTAL_SNIPPETS = 25;

    const totalSnippetsRequested = computed(() =>
      form.value.selectedShows.reduce((sum, showId) => {
        const config = snippetShowConfigs.value[showId];
        return sum + (Number(config?.snippets_count) || 0);
      }, 0)
    );

    const perEpisodeExceedsTotal = (showId) => {
      const config = snippetShowConfigs.value[showId];
      return (
        !!config &&
        Number(config.snippets_per_episode) > Number(config.snippets_count)
      );
    };

    const snippetShowsValid = computed(
      () =>
        form.value.contentMediaType !== "snippet" ||
        form.value.contentType !== "shows" ||
        (totalSnippetsRequested.value <= MAX_TOTAL_SNIPPETS &&
          form.value.selectedShows.every(
            (showId) => !perEpisodeExceedsTotal(showId)
          ))
    );

    watch(
      () => form.value.selectedShows,
      (newShows) => {
        if (isEditLoad) return;

        const showById = new Map(
          podcastShowsOptions.value.map((show) => [show.value, show])
        );
        const newConfigs = {};
        for (const showId of newShows || []) {
          const showMaxPerEpisode =
            showById.get(showId)?.snippetsPerEpisodeConfig || 1;
          newConfigs[showId] = snippetShowConfigs.value[showId] || {
            snippets_count: 1,
            snippets_per_episode: showMaxPerEpisode,
          };
        }
        snippetShowConfigs.value = newConfigs;
      },
      { deep: true }
    );

    const sellerId = router.currentRoute.value.params.sellerId;

    if (sellerId) {
      form.value.sellerId = sellerId;
    }

    const contactValueLabel = computed(() => {
      if (form.value.contractType === "cpm") {
        return "CPM Rate";
      } else {
        return "Share Percent";
      }
    });

    const selectedSellerGroup = computed(() => {
      return sellerGroups.value.find(
        (item) => item.value == form.value.sellerGroup
      );
    });

    const shouldShowConnatixFields = computed(() => {
      const group = selectedSellerGroup.value;
      return Boolean(
        group?.video_monetization_enabled && group?.connatix_reporting_enabled
      );
    });

    // Ads.txt lines for the seller's domain: audio-only by default,
    // + video for video-enabled sellers; top tier (P1+P2) by default
    const adsTxtRows = ref([]);
    const adsTxtTier = ref("top");
    const adsTxtTierOptions = [
      { label: "Top Tier (P1+P2)", value: "top" },
      { label: "All Lines (P1+P2+P3)", value: "all" },
    ];

    const generatedAdsTxtLines = computed(() => {
      const types = shouldShowConnatixFields.value
        ? ["audio", "video"]
        : ["audio"];
      const maxPriority = adsTxtTier.value === "all" ? 3 : 2;
      return adsTxtRows.value
        .filter(
          (row) =>
            types.includes(row.type) &&
            (Number(row.priority) || 3) <= maxPriority
        )
        .map((row) => row.row_string)
        .join("\n");
    });

    const fetchAdsTxtRows = async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/adstxt-rows`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        adsTxtRows.value = data.result || [];
      } catch (error) {
        console.error("Error fetching ads.txt rows:", error);
      }
    };

    const copyAdsTxtLines = () => {
      navigator.clipboard
        .writeText(generatedAdsTxtLines.value)
        .then(() => {
          toast.add({
            severity: "success",
            summary: "Copied!",
            detail: "Ads.txt lines copied to clipboard",
            life: 2000,
          });
        })
        .catch(() => {
          toast.add({
            severity: "error",
            summary: "Error",
            detail: "Failed to copy to clipboard",
            life: 3000,
          });
        });
    };

    const removeGeo = (index) => {
      form.value.cpm_per_geo.splice(index, 1);
    };

    const filterShowsByPublisherId = (publisherId) => {
      if (!publisherId) return false;

      const connectedPodcasterId = sellerGroups.value.find(
        (item) => item.value == publisherId
      )?.connected_podcaster_id;

      if (!connectedPodcasterId) return false;

      return allShowsList.value.filter(
        (item) => item.podcaster_id == connectedPodcasterId
      );
    };

    const checkIfGroupConnectedPodcaster = (groupId) => {
      if (groupId) {
        const filteredShowOptions = filterShowsByPublisherId(groupId);
        if (filteredShowOptions) {
          buildShowOptions(filteredShowOptions);
          contentShowsOnly.value = true;
          form.value.selectedShows = form.value.selectedShows.filter((item) =>
            podcastShowsOptions.value.some((show) => show.value == item)
          );
          form.value.contentType = contentTypes.value[0].value;
        } else {
          contentShowsOnly.value = false;
          buildShowOptions(allShowsList.value);
        }
      }
    };

    const buildShowOptions = (showsList) => {
      const filteredList =
        form.value.contentMediaType === "snippet"
          ? showsList.filter((show) => (show.snippet_count || 0) > 0)
          : showsList;

      podcastShowsOptions.value = filteredList.map((show) => {
        const generationConfig = show.snippets_generation_config
          ? typeof show.snippets_generation_config === "string"
            ? JSON.parse(show.snippets_generation_config)
            : show.snippets_generation_config
          : null;

        return {
          label: show.show_title,
          value: `${show.show_id}`,
          podcaster_id: show.podcaster_id,
          snippet_count: show.snippet_count || 0,
          snippetsPerEpisodeConfig: generationConfig?.snippets_per_episode || 1,
        };
      });
    };

    const fetchPodcastShows = async () => {
      try {
        store.commit("setLoading", true);
        const response = await axios.get(API_BASE_URL + "/podcast-shows", {
          headers: { Authorization: `Bearer ${token}` },
          params:
            form.value.contentMediaType === "snippet"
              ? { snippetsOnly: true }
              : {},
        });

        const labelCounts = {};

        allShowsList.value = response.data.map((obj) => {
          if (!labelCounts[obj.show_title]) {
            labelCounts[obj.show_title] = 0; // Initialize count
          }
          labelCounts[obj.show_title] += 1;

          // Append the count to the label if it's a duplicate
          const newLabel =
            labelCounts[obj.show_title] > 1
              ? `${obj.show_title}_${labelCounts[obj.show_title] - 1}`
              : obj.show_title;

          return { ...obj, show_title: newLabel };
        });

        buildShowOptions(allShowsList.value);

        if (form.value.sellerGroup)
          checkIfGroupConnectedPodcaster(form.value.sellerGroup);
      } catch (error) {
        console.error("Error fetching podcast shows:", error);
      } finally {
        store.commit("setLoading", false);
      }
    };

    const confirmRSSConsentBeforeSubmit = () => {
      if (
        form.value.contentType === "rss_feed" &&
        (initialRSSUrl.value !== form.value.rssFeedUrl ||
          !form.value.consent_approved)
      ) {
        confirm.require({
          message:
            'To monetize your content on our platform, we request your permission to place ads on your RSS feed. By clicking "I Agree" below, you confirm that you allow JamX to monetize your content in the programmatic marketplace.',
          header: "Attention",
          icon: "pi pi-check-square",
          acceptLabel: "I Agree",
          rejectLabel: "Deny",
          accept: () => {
            form.value.consent_approved = true;
            submitForm();
          },
          reject: async () => {
            form.value.consent_approved = false;
            await submitForm();
            toast.add({
              severity: "error",
              summary: "Rejected",
              detail: "You have rejected the consent agreement.",
              life: 5000,
            });
          },
        });
      } else {
        submitForm();
      }
    };

    const confirmCPMHighBeforeSubmit = () => {
      if (
        form.value.contractType === "cpm" &&
        initialContractValue.value < 2 &&
        form.value.contractValue >= 2
      ) {
        confirm.require({
          message:
            "You have increased the CPM to over $2. Please confirm your agreement with this change.",
          header: "Attention",
          icon: "pi pi-exclamation-circle",
          acceptLabel: "Confirm",
          rejectLabel: "Cancel",
          accept: () => {
            setTimeout(() => {
              confirmRSSConsentBeforeSubmit();
            }, 300);
          },
        });
      } else {
        confirmRSSConsentBeforeSubmit();
      }
    };

    const fetchPlaylists = async () => {
      try {
        store.commit("setLoading", true);
        const response = await axios.get(API_BASE_URL + "/playlists", {
          headers: { Authorization: `Bearer ${token}`, partnerid: partnerId },
        });
        playlistsOptions.value = response.data.playlists.filter(
          (item) => item.status == "ready"
        );
      } catch (error) {
        // Handle errors, e.g., show an error message
        console.error("Error fetching playlists:", error);
      } finally {
        store.commit("setLoading", false);
      }
    };

    const fetchCountryList = async () => {
      try {
        store.commit("setLoading", true);
        const response = await axios.get(
          "https://restcountries.com/v3.1/all?fields=name,cca2"
        );
        if (response.data) {
          countryList.value = response.data;
        }
      } catch (error) {
        console.error("Error fetching countries:", error);
      } finally {
        store.commit("setLoading", false);
      }
    };

    let isEditLoad = false;

    const fetchSubSeller = async () => {
      try {
        store.commit("setLoading", true);
        if (!sellerId) return;

        isEditLoad = true;
        const response = await axios.get(API_BASE_URL + "/seller/" + sellerId, {
          headers: {
            Authorization: `Bearer ${token}`,
            partnerid: partnerId,
          },
        });
        if (response.data.success) {
          const seller = response.data.seller;

          let categoriesObject = [];
          let siteCategoriesObject = [];

          if (seller.categories?.length && seller.categories !== "null") {
            categoriesObject = JSON.parse(seller.categories);
          }
          if (
            seller.site_categories?.length &&
            seller.site_categories !== "null"
          ) {
            siteCategoriesObject = JSON.parse(seller.site_categories);
          }

          form.value.geo_bl = seller.geo_bl?.length
            ? seller.geo_bl.toUpperCase().split(",")
            : [];
          form.value.geo_wl = seller.geo_wl?.length
            ? seller.geo_wl.toUpperCase().split(",")
            : [];
          form.value.name = seller.name;
          form.value.email = seller.email;
          form.value.domain = seller.domain;
          form.value.categories = categoriesObject;

          form.value.contractType = seller.contract_type;

          initialContractValue.value =
            seller.contract_type == "cpm" ? seller.contract_value : 0;
          form.value.contractValue =
            seller.contract_type == "cpm"
              ? seller.contract_value
              : seller.contract_value * 100;
          form.value.contentMediaType = seller.content_media_type || "podcast";
          form.value.restrictedValidation = !!seller.restricted_validation;
          form.value.ctaMode = seller.cta_mode || "want_more";
          form.value.contentType = seller.content_type;
          form.value.min_episode_duration = seller.min_episode_duration ?? 0;
          form.value.rssFeedUrl = seller.rss_feed;
          form.value.playlists = seller.playlist_id;
          form.value.sellerGroup = seller.group_id;
          form.value.custom_slogan_text = seller.custom_slogan_text;
          form.value.custom_slogan_link = seller.custom_slogan_link;
          form.value.consent_approved = seller.consent_approved;
          form.value.connatix_player_id = seller.connatix_player_id || "";
          form.value.connatix_player_share = normalizeConnatixShareValue(
            seller.connatix_player_share
          );
          form.value.selectedShows = seller.show_ids_content
            ? seller.show_ids_content.trim().startsWith("[")
              ? JSON.parse(seller.show_ids_content)
              : seller.show_ids_content.split(",")
            : [];

          const parsedAllocationConfig = seller.content_allocation_config
            ? typeof seller.content_allocation_config === "string"
              ? JSON.parse(seller.content_allocation_config)
              : seller.content_allocation_config
            : { shows: [], display_order: { shuffle: false } };
          snippetShowConfigs.value = Object.fromEntries(
            (parsedAllocationConfig.shows || []).map((entry) => [
              entry.show_id,
              {
                snippets_count: entry.snippets_count ?? 1,
                snippets_per_episode: entry.snippets_per_episode,
              },
            ])
          );
          displayOrder.value = parsedAllocationConfig.display_order || {
            shuffle: false,
          };

          if (parsedAllocationConfig.shows?.length) {
            const configOrder = parsedAllocationConfig.shows.map(
              (entry) => `${entry.show_id}`
            );
            const remaining = form.value.selectedShows.filter(
              (id) => !configOrder.includes(id)
            );
            form.value.selectedShows = [
              ...configOrder.filter((id) =>
                form.value.selectedShows.includes(id)
              ),
              ...remaining,
            ];
          }

          initialRSSUrl.value = seller.rss_feed;
          form.value.tier = seller.tier;
          form.value.classification = seller.seller_classification;
          form.value.site_categories = siteCategoriesObject;
          if (seller.cpm_per_geo?.length)
            form.value.cpm_per_geo = JSON.parse(seller.cpm_per_geo);
        }
      } catch (error) {
        console.error("Error fetching sub-seller:", error);
      } finally {
        nextTick(() => {
          isEditLoad = false;
        });
        store.commit("setLoading", false);
      }
    };

    const onContractTypeChange = () => {
      // Reset the contract value when the contract type changes
      form.value.contractValue = 0;
    };

    const contactValidate = computed(() => {
      return (
        form.value.contractType === "revshare" ||
        (form.value.contractType === "cpm" &&
          form.value.contractValue &&
          form.value.contractValue > 0)
      );
    });

    const validateEmail = () => {
      const re =
        // eslint-disable-next-line no-useless-escape
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      emailValid.value = re.test(String(form.value.email).toLowerCase());
    };

    const validateDomain = () => {
      // eslint-disable-next-line no-useless-escape
      const re =
        /^(?=.{1,253}\.?$)(?:(?!-|[^.]+_)[A-Za-z0-9-_]{1,63}(?<!-)(?:\.|$)){2,}$/;
      domainValid.value = re.test(String(form.value.domain).toLowerCase());
    };

    const validateContractDetails = () => {
      if (form.value.contractType === "cpm") {
        return (
          (!form.value.cpm_per_geo.length ||
            form.value.cpm_per_geo.every(
              (item) => item.geo?.length && item.cpm >= 0
            )) &&
          form.value.contractValue >= 0
        );
      } else if (form.value.contractType === "revshare") {
        return true;
      }
      return false;
    };

    const validateRssFeed = (urlStr) => {
      let url;

      try {
        url = new URL(urlStr);
      } catch (_) {
        return false;
      }

      return url.protocol === "http:" || url.protocol === "https:";
    };

    const validatePassword = () => {
      passwordValid.value = !(
        !form.value.password ||
        form.value.password.length < 8 ||
        !(
          /[a-z]/.test(form.value.password) && /[A-Z]/.test(form.value.password)
        )
      );
    };

    const normalizeConnatixShareValue = (value) => {
      if (value === null || value === undefined || value === "") return null;
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) return null;
      return numericValue > 1 ? numericValue / 100 : numericValue;
    };

    const isSelectable = (node) => {
      return !node.children;
    };

    const validateConnatixFields = () => {
      if (!shouldShowConnatixFields.value) {
        connatixPlayerIdValid.value = true;
        connatixPlayerShareValid.value = true;
        return true;
      }

      connatixPlayerIdValid.value =
        form.value.connatix_player_id !== "" &&
        form.value.connatix_player_id !== null &&
        form.value.connatix_player_id !== undefined;
      const rawShare = form.value.connatix_player_share;
      const share =
        rawShare === "" || rawShare == null ? null : Number(rawShare);
      connatixPlayerShareValid.value =
        share === null ||
        (Number.isFinite(share) && share >= 0.01 && share <= 1);

      return connatixPlayerIdValid.value && connatixPlayerShareValid.value;
    };

    const submitForm = async () => {
      submitted.value = true;
      validateEmail();
      validateDomain();

      if (!sellerId) validatePassword();
      else passwordValid.value = true;

      rssFeedUrlValid.value = true;
      if (form.value.contentType == "rss_feed") {
        rssFeedUrlValid.value = validateRssFeed(form.value.rssFeedUrl);
      }

      if (form.value.contentType === "shows") {
        form.value.show_ids_content = form.value.selectedShows.join(",");
      }

      if (form.value.contentMediaType === "snippet") {
        form.value.contentAllocationConfig = {
          shows: form.value.selectedShows.map((showId) => ({
            show_id: showId,
            snippets_count: snippetShowConfigs.value[showId]?.snippets_count,
            snippets_per_episode:
              snippetShowConfigs.value[showId]?.snippets_per_episode,
          })),
          display_order: displayOrder.value,
        };
      }

      if (
        form.value.name &&
        domainValid.value &&
        (emailValid.value || form.value.sellerGroup) &&
        (passwordValid.value || form.value.sellerGroup)
      ) {
        if (
          !rssFeedUrlValid.value ||
          !snippetShowsValid.value ||
          !minEpisodeDurationValid.value ||
          ((!validateContractDetails() || !form.value.sellerGroup) &&
            !store.getters.isGroupAdminLoggedIn)
        )
          return;
        if (!validateConnatixFields()) return;
        try {
          store.commit("setLoading", true);
          const response = await axios.post(
            API_BASE_URL + "/add-seller",
            form.value,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                partnerid: partnerId,
              },
            }
          );

          if (response.data?.success) {
            // Clear form after successful submission
            form.value = {
              name: "",
              email: "",
              domain: "",
              categories: [],
              contractType: "",
              contractValue: "",
              contentMediaType: "podcast",
              restrictedValidation: false,
              ctaMode: "want_more",
              contentType: "",
              min_episode_duration: 0,
              rssFeedUrl: "",
              password: "",
              sellerGroup: null,
              custom_slogan_link: "",
              custom_slogan_text: "",
              consent_approved: false,
              site_categories: [],
              tier: "3",
              cpm_per_geo: [],
              connatix_player_id: "",
              connatix_player_share: null,
            };

            submitted.value = false;

            toast.add({
              severity: "success",
              summary: "Success",
              detail: `Seller ${sellerId ? "updated" : "added"} successfully!`,
              life: 4000,
            });
            if (response.data?.autoPending) {
              toast.add({
                severity: "warn",
                summary: "Status Changed",
                detail: "Seller status has been changed to Pending.",
                life: 5000,
              });

              console.log("Resposne", response.data);
            }
            router.go(-1);
            // Show a success message, or redirect to another page, etc.
          } else {
            // Handle unsuccessful submission
            toast.add({
              severity: "error",
              summary: "Error",
              detail: response.data.message,
              life: 4000,
            });
          }
        } catch (error) {
          // Handle errors
          toast.add({
            severity: "error",
            summary: "Error",
            detail: response.data.message,
            life: 4000,
          });
          console.log(error);
        } finally {
          store.commit("setLoading", false);
        }
      }
    };

    const fetchCategories = async () => {
      try {
        store.commit("setLoading", true);
        const response = await axios.get(API_BASE_URL + "/categories", {
          headers: { Authorization: `Bearer ${token}` },
        });
        categories.value = response.data;
      } catch (error) {
        // Handle errors, e.g., show an error message
        console.log("Error during categories fetch", error);
      } finally {
        store.commit("setLoading", false);
      }
    };

    const fetchGroups = async () => {
      try {
        store.commit("setLoading", true);
        const response = await axios.get(API_BASE_URL + "/seller-groups", {
          headers: { Authorization: `Bearer ${token}`, partnerid: partnerId },
        });

        sellerGroups.value = response.data.sellerGroups.map((group) => {
          return {
            label: group.name,
            value: group.id,
            connected_podcaster_id: group.connected_podcaster_id,
            video_monetization_enabled: group.video_monetization_enabled,
            connatix_reporting_enabled: group.connatix_reporting_enabled,
          };
        });
        await fetchPodcastShows();
        fetchSubSeller();
      } catch (error) {
        // Handle errors, e.g., show an error message
      } finally {
        store.commit("setLoading", false);
      }
    };

    onMounted(fetchPlaylists);
    onMounted(fetchCategories);
    onMounted(fetchGroups);
    onMounted(fetchCountryList);
    onMounted(fetchAdsTxtRows);

    watch(() => form.value.sellerGroup, checkIfGroupConnectedPodcaster);
    watch(
      () => form.value.contentMediaType,
      () => {
        if (
          !contentTypes.value.some(
            (item) => item.value === form.value.contentType
          )
        ) {
          form.value.contentType = contentTypes.value[0].value;
        }
        fetchPodcastShows();

        if (isEditLoad) return;

        form.value.selectedShows = [];
      }
    );
    watch(shouldShowConnatixFields, (enabled) => {
      if (!enabled) {
        form.value.connatix_player_id = "";
        form.value.connatix_player_share = null;
        connatixPlayerIdValid.value = true;
        connatixPlayerShareValid.value = true;
      }
    });

    return {
      form,
      store,
      router,
      categories,
      submitForm,
      contractTypes,
      submitted,
      emailValid,
      domainValid,
      contractValueValid,
      onContractTypeChange,
      contentTypes,
      rssFeedUrlValid,
      validateRssFeed,
      isSelectable,
      searchText,
      contactValidate,
      sellerGroups,
      validatePassword,
      contactValueLabel,
      passwordValid,
      sellerId,
      playlistsOptions,
      confirmRSSConsentBeforeSubmit,
      confirmCPMHighBeforeSubmit,
      countryList,
      podcastShowsOptions,
      snippetShowConfigs,
      displayOrder,
      MAX_TOTAL_SNIPPETS,
      totalSnippetsRequested,
      snippetShowsValid,
      perEpisodeExceedsTotal,
      showMinEpisodeDuration,
      minEpisodeDurationValid,
      tierOptions,
      classificationOptions,
      removeGeo,
      validateContractDetails,
      shouldShowConnatixFields,
      connatixPlayerIdValid,
      connatixPlayerShareValid,
      adsTxtTier,
      adsTxtTierOptions,
      generatedAdsTxtLines,
      copyAdsTxtLines,
    };
  },
};
</script>

<style scoped>
.adstxt-code-container {
  background: var(--surface-50);
  border: 1px solid var(--surface-border);
  border-radius: 4px;
  padding: 0.75rem 1rem;
  max-height: 300px;
  overflow: auto;
  font-family: "Courier New", monospace;
  font-size: 0.85rem;
  margin-bottom: 0.5rem;
}

.adstxt-code-container pre {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-all;
}

.drag-handle {
  cursor: grab;
  color: var(--text-color-secondary, #9ca3af);
  font-size: 0.9rem;
}

.drag-handle:active {
  cursor: grabbing;
}

.show-order-card {
  border: 1px solid var(--surface-border);
  border-left: 4px solid var(--primary-color);
  border-radius: 12px;
  padding: 1rem 1.25rem;
}

.show-order-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.show-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--primary-color);
}

.show-title {
  font-weight: 600;
}

.show-position-badge {
  background: color-mix(in srgb, var(--primary-color) 12%, transparent);
  color: var(--primary-color);
  font-size: 0.75rem;
  font-weight: 600;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.show-order-fields {
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.show-field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  flex: 1;
  min-width: 8rem;
}

.show-order-card :deep(.p-inputnumber-input) {
  width: 100%;
  min-width: 0;
}
</style>
